import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { ArrowLeft, LogIn } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { startWhop } from '../lib/whop';
import api from '../lib/api';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Logo } from '../components/Logo';

export default function Login() {
  const { user, access, loading, loginWithToken } = useAuth();
  const navigate = useNavigate();
  const [showStaff, setShowStaff] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [otcInput, setOtcInput] = useState('');
  const [otcBusy, setOtcBusy] = useState(false);

  // The manual OTC box is only useful in the split-origin preview. When this web app is deployed
  // on the SAME domain as the backend, the Whop callback auto-lands on the SPA and Landing captures
  // the code automatically — so hide the manual box there.
  const splitOrigin = (() => {
    try {
      return new URL(process.env.REACT_APP_BACKEND_URL).origin !== window.location.origin;
    } catch {
      return true;
    }
  })();

  useEffect(() => {
    if (!loading && user && access) navigate('/app', { replace: true });
  }, [loading, user, access, navigate]);

  // Manual Whop completion for the split-origin preview: the backend web callback redirects to
  // the backend domain (…/?whop_otc=CODE). Paste that URL (or the code) here to finish sign-in.
  const completeWhop = async (e) => {
    e.preventDefault();
    const raw = otcInput.trim();
    if (!raw) return;
    const m = raw.match(/(?:whop_otc|otc)=([^&\s]+)/);
    const otc = m ? decodeURIComponent(m[1]) : raw;
    setOtcBusy(true);
    try {
      const { data } = await api.post('/auth/whop/complete', { otc });
      await loginWithToken(data.token);
      navigate('/app', { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Invalid or expired Whop code');
    } finally {
      setOtcBusy(false);
    }
  };

  const staffLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post('/auth/login', { email, password });
      await loginWithToken(data.token);
      navigate('/app', { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Invalid credentials');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-screen bg-ink-base text-white lg:grid-cols-2">
      <div className="grain relative hidden flex-col justify-between overflow-hidden border-r border-white/10 bg-ink-surface p-12 lg:flex">
        <Link to="/" className="flex items-center gap-2">
          <Logo size={38} />
        </Link>
        <div>
          <h2 className="font-heading text-4xl font-extrabold tracking-tighter">
            Welcome back, <span className="text-emerald">trader.</span>
          </h2>
          <p className="mt-4 max-w-sm text-zinc-400">
            Sign in through Whop to unlock the full member area — lessons, courses, progress and tools.
          </p>
        </div>
        <div className="text-xs text-zinc-600">Access secured & verified via Whop entitlements.</div>
      </div>

      <div className="flex flex-col justify-center px-6 py-16 lg:px-20">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mx-auto w-full max-w-sm"
        >
          <Link to="/" className="mb-8 inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-white" data-testid="back-home-link">
            <ArrowLeft size={16} /> Back
          </Link>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Sign in</h1>
          <p className="mt-2 text-sm text-zinc-400">Continue with your Whop membership.</p>

          <Button pill size="lg" variant="emerald" className="mt-8 w-full" onClick={startWhop} data-testid="whop-login-button">
            <LogIn size={18} /> Continue with Whop
          </Button>

          <div className="my-8 flex items-center gap-3 text-xs text-zinc-600">
            <div className="h-px flex-1 bg-white/10" /> staff access <div className="h-px flex-1 bg-white/10" />
          </div>

          {!showStaff ? (
            <button
              onClick={() => setShowStaff(true)}
              data-testid="show-staff-login"
              className="w-full text-center text-sm text-zinc-500 underline-offset-4 hover:text-white hover:underline"
            >
              Sign in with email &amp; password
            </button>
          ) : (
            <form onSubmit={staffLogin} className="space-y-3">
              <Input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email-input"
              />
              <Input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password-input"
              />
              <Button type="submit" size="lg" className="w-full" disabled={busy} data-testid="login-submit-button">
                {busy ? 'Signing in…' : 'Sign in'}
              </Button>
            </form>
          )}

          <div className={`mt-8 rounded-xl border border-white/5 bg-ink-surface p-4 ${splitOrigin ? '' : 'hidden'}`}>
            <p className="text-xs font-medium text-zinc-300">Finish Whop sign-in (preview only)</p>
            <p className="mt-1 text-[11px] leading-relaxed text-zinc-500">
              After “Continue with Whop”, your browser lands on the backend domain at
              <span className="text-zinc-400"> …/?whop_otc=CODE</span>. Copy that URL (or the code) and paste it below.
            </p>
            <form onSubmit={completeWhop} className="mt-3 flex gap-2">
              <Input
                placeholder="Paste redirect URL or code"
                value={otcInput}
                onChange={(e) => setOtcInput(e.target.value)}
                className="h-10"
                data-testid="whop-otc-input"
              />
              <Button type="submit" size="sm" variant="emerald" disabled={otcBusy} data-testid="whop-otc-submit">
                {otcBusy ? '…' : 'Finish'}
              </Button>
            </form>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
